import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import {
  PROVIDER_OFFER_POLL_MS,
  fetchActiveProviderOffers,
  pickNewestActiveOffer,
} from '@/lib/providerDispatch';
import { incomingOfferPath } from '@/lib/providerPush';
import {
  setActiveDispatchOffer,
  stopDispatchRingtone,
} from '@/lib/dispatchRingtone';

/**
 * While a provider is on dashboard / other BottomNav screens, poll existing
 * GET /providers/me/offers every 5s and open the existing offer detail UI.
 * The same poll is the authoritative liveness signal for the foreground
 * dispatch ringtone: when the active pending/viewed offer disappears, the
 * looping ringtone is stopped immediately.
 */
export default function useProviderIncomingOfferPoll(enabled) {
  const navigate = useNavigate();
  const openedOfferIdRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;

    const tick = async () => {
      try {
        const offers = await fetchActiveProviderOffers(api);
        if (cancelled) return;
        const newest = pickNewestActiveOffer(offers);
        if (!newest?.id) {
          // No actionable offers: silence the ringer and allow future
          // legitimate offers to open normally.
          openedOfferIdRef.current = null;
          stopDispatchRingtone('no-active-offers');
          return;
        }
        setActiveDispatchOffer(newest.id);
        if (openedOfferIdRef.current === newest.id) return;
        openedOfferIdRef.current = newest.id;
        navigate(incomingOfferPath(newest.id));
      } catch {
        // Keep polling; a failed tick must not break the provider session
        // or stop a still-ringing offer on a transient network error.
      }
    };

    tick();
    const timer = setInterval(tick, PROVIDER_OFFER_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [enabled, navigate]);
}
